"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 87: J87 (Konami / Jaleco / Taito)
 * ------------------------------------------------------------------ *
 * The first board to get an iNES mapper number. Goonies (J), Hyper Olympic
 * (J) and a handful of other Famicom games use it.
 *
 *   - PRG ROM: 32 KB fixed at $8000-$FFFF. A 16 KB image is mirrored into
 *     both halves, which is what the header's one PRG bank means.
 *   - CHR ROM: one 8 KB window at $0000-$1FFF, selected by a write anywhere
 *     in $6000-$7FFF. Most boards carry 16 KB of CHR, the JF-09/JF-10 boards
 *     32 KB; the register still exposes both bits.
 *   - Mirroring is hard-wired by solder pads, so the header is authoritative.
 *
 * Register (https://www.nesdev.org/wiki/INES_Mapper_087):
 *
 *      7  bit  0
 *      ---- ----
 *      xxxx xxLH
 *             ||
 *             |+- CHR A14 (high bit)
 *             +-- CHR A13 (low bit)
 *
 * The two bits are crossed relative to the bank number: writing 2 selects
 * 8 KB bank 1, writing 1 selects bank 2. Mapper 101 is the same board with the
 * normal bit order, for dumps whose CHR bit order was reversed.
 *
 * There is no PRG RAM on this board, so the register window must not fall
 * through to NROM's write path: that would leave a stray byte in cpu.mem at
 * $6000 and hide the fact that the write was a bank select.
 * ------------------------------------------------------------------ */

function register(Mappers) {
  Mappers[87] = function (nes) {
    this.nes = nes;
  };

  Mappers[87].prototype = new Mappers[0]();

  Mappers[87].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);
    this.chrBank = 0;
  };

  Mappers[87].prototype.write = function (address, value) {
    if (address >= 0x6000 && address <= 0x7fff) {
      // xxxx xxLH: bit 0 is the high CHR bit, bit 1 the low one.
      var bank = ((value & 0x01) << 1) | ((value >> 1) & 0x01);
      this.chrBank = bank;
      this.load8kVromBank(bank * 2, 0x0000);
      return;
    }

    if (address < 0x6000) {
      // RAM, PPU/APU registers and expansion space: NROM's write path.
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    // $8000-$FFFF is PRG ROM. A write there reaches no register, and falling
    // through to NROM would overwrite the ROM copy in cpu.mem.
  };

  Mappers[87].prototype.updateBanks = function () {
    var rom = this.nes.rom;
    if (!rom || rom.romCount === 0) {
      return;
    }

    // 32 KB of PRG, fixed. A one-bank image (16 KB) is mirrored.
    if (rom.romCount > 1) {
      this.loadRomBank(0, 0x8000);
      this.loadRomBank(1, 0xc000);
    } else {
      this.loadRomBank(0, 0x8000);
      this.loadRomBank(0, 0xc000);
    }

    // The register counts 8 KB banks; load8kVromBank() wants the first 4 KB
    // bank of the pair, hence the * 2. It wraps against the real CHR size, so
    // the high bit is harmless on a 16 KB board.
    this.load8kVromBank(this.chrBank * 2, 0x0000);
  };

  Mappers[87].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Mapper 87: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on CHR bank 0, as the board comes out of reset.
    this.chrBank = 0;
    this.updateBanks();

    this.resetIrqHandler();
  };

  Mappers[87].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.chrBank = this.chrBank;
    return s;
  };

  Mappers[87].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.chrBank = s.chrBank;
    this.updateBanks();
  };
}

module.exports = { register: register };
